import { ExternalSharedReportHttpAuthInterceptor } from '@aca-new/app/pages/reports/pages/external-shared-report/shared/services/external-shared-report-http-auth-interceptor.service';
import { AI_API_ACCESS_TOKEN } from '@aca-new/app/shared/constants/ai-api-access-token.constants';
import { USE_SESSION_HTTP_AUTH } from '@aca-new/app/shared/constants/http-context.tokens';
import { PATHS } from '@aca-new/app/shared/constants/url-path.constants';
import { HttpAuthInterceptor } from '@aca-new/app/shared/services/http-services/http-auth-handler/http-auth-interceptor.service';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  public constructor(
    private readonly _router: Router,
    private readonly _httpAuthInterceptor: HttpAuthInterceptor,
    private readonly _externalReportHttpAuthInterceptor: ExternalSharedReportHttpAuthInterceptor
  ) {}

  public intercept<T>(request: Readonly<HttpRequest<T>>, next: Readonly<HttpHandler>): Observable<HttpEvent<T>> {
    const unauthorizedArray = ['/assets/', '/common-payment', 'COMMON_PAYMENT_BU_MERCHANT_ID_MAPPING'];
    const isExternalReportRoute = this._router.url.startsWith(PATHS.externalReport);

    // Public supplier-confirmation and supplier-confirmation-hub pages (no user login).
    if (this._isPublicSupplierConfirmationRoute()) {
      request = request.clone({
        setHeaders: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'ai-api-access-token': AI_API_ACCESS_TOKEN,
        },
      });

      return next.handle(request);
    }

    if (this._router.url.startsWith('/pay/') || this._router.url.startsWith('/pay-pre-filled/')) {
      unauthorizedArray.push('/payment/adyen/');
      unauthorizedArray.push('/finance/payment/action-logs');
      unauthorizedArray.push('CERTIFICATION_LANGUAGE_PREFERENCES');
    }

    if (this._router.url.startsWith('/campaign/reject')) {
      unauthorizedArray.push('/parameter/v2/');
      unauthorizedArray.push('/campaign/');
      unauthorizedArray.push('/audit/renewal/reject');
    }

    // Address info keyword search is public (no user login); used on supplier confirmation too.
    if (request.url.includes('/parameter/v2/geo/location/search')) {
      return next.handle(
        request.clone({
          setHeaders: {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'ai-api-access-token': AI_API_ACCESS_TOKEN,
          },
        })
      );
    }

    if (request.url.includes('/v1.0/system-event/spec')) {
      if (isExternalReportRoute) {
        return this._externalReportHttpAuthInterceptor.intercept(request, next);
      }

      request = request.clone({
        setHeaders: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'ai-api-access-token': AI_API_ACCESS_TOKEN,
        },
      });

      return next.handle(request);
    }

    if (!request?.url || unauthorizedArray.some((url: string): boolean => request?.url.includes(url))) {
      return next.handle(request);
    }

    if (request.context.get(USE_SESSION_HTTP_AUTH)) {
      return this._httpAuthInterceptor.intercept(request, next);
    }

    return this._getInterceptor(isExternalReportRoute).intercept(request, next);
  }

  /**
   * @returns {boolean} True when the current route is a public supplier-confirmation flow page.
   */
  private _isPublicSupplierConfirmationRoute(): boolean {
    const path = this._router.url.split(/[?#]/)[0];

    return (
      path === '/supplier-confirmation' || path.startsWith('/supplier-confirmation/') || path === '/supplier-confirmation-hub' || path.startsWith('/supplier-confirmation-hub/')
    );
  }

  private _getInterceptor(isExternalReportRoute: boolean): HttpInterceptor {
    if (isExternalReportRoute) {
      return this._externalReportHttpAuthInterceptor;
    }

    return this._httpAuthInterceptor;
  }
}
